import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingPeriodToggle } from '@/features/subscriptions/components/billing-period-toggle';
import { PricingCard } from '@/features/subscriptions/components/pricing-card';
import { organizationPlans } from '@/features/subscriptions/data/organization-plans';
import { useCanManageSubscriptions } from '@/features/subscriptions/hooks/use-can-manage-subscriptions';
import { useOrganizationPlansDialog } from '@/features/subscriptions/hooks/use-organization-plans-dialog';
import { BillingPeriod } from '@/features/subscriptions/types/subscription-types';
import WarningTriangleIcon from '@/shared/assets/warning-triangle.png';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';

export const OrganizationPlansDialog = () => {
  const { t } = useTranslation('subscriptions');
  const isOpen = useOrganizationPlansDialog((state) => state.isOpen);
  const close = useOrganizationPlansDialog((state) => state.close);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const canManageSubscriptions = useCanManageSubscriptions();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-[1200px] border border-white/10 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f1729] p-0">
        <div className="flex flex-col gap-8 p-8">
          {canManageSubscriptions ? (
            <>
              <div className="flex flex-col items-center gap-6 text-center">
                <div className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5">
                  <div className="size-2 rounded-full bg-custom-green-glow" />
                  <span className="text-xs text-white/70">
                    {t('organizationPlans.badge')}
                  </span>
                </div>

                <div className="space-y-3">
                  <DialogTitle className="text-4xl font-semibold text-white">
                    {t('organizationPlans.title')}
                  </DialogTitle>
                  <DialogDescription className="mx-auto max-w-2xl text-sm text-white/60">
                    {t('organizationPlans.description')}
                  </DialogDescription>
                </div>

                <BillingPeriodToggle
                  billingPeriod={billingPeriod}
                  onBillingPeriodChange={setBillingPeriod}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {organizationPlans.map((plan) => (
                  <PricingCard
                    key={plan.id}
                    plan={plan}
                    billingPeriod={billingPeriod}
                  />
                ))}
              </div>

              <div className="border-t border-white/10 pt-6">
                <div className="flex flex-col items-center gap-4 text-center">
                  <p className="max-w-2xl text-sm text-white/70">
                    {t('organizationPlans.contactSales')}
                  </p>
                  <Button
                    variant="outline"
                    className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                  >
                    {t('buttons.contactSales')}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <img
                  src={WarningTriangleIcon}
                  alt="warning"
                  className="size-6 object-contain"
                />
                <div className="flex flex-1 flex-col gap-2">
                  <DialogTitle className="text-2xl font-semibold text-white">
                    {t('paywall.memberRestriction.title')}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-white/60">
                    {t('paywall.memberRestriction.description')}
                  </DialogDescription>
                </div>
              </div>
              <div className="flex justify-center border-t border-white/10 pt-6">
                <Button
                  variant="outline"
                  className="min-w-40 border-white/20 bg-white/5 text-white hover:bg-white/10"
                  onClick={close}
                >
                  OK
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
